import { UserProfile, UserRole, UserStatus } from "../types";

export type PermissionKey =
  | "projects.view"
  | "projects.edit"
  | "project_settings.manage"
  | "project_show.view"
  | "project_show.manage"
  | "projects.export"
  | "wiki.view"
  | "wiki.edit"
  | "wiki.manage"
  | "crm.view"
  | "crm.edit"
  | "people.view"
  | "people.manage"
  | "calendar.view"
  | "calendar.manage"
  | "settings.view"
  | "settings.manage"
  | "integrations.manage"
  | "drive.manage"
  | "users.manage"
  | "trash.manage";

export const ACCESS_ROLE_OPTIONS: Array<{ value: UserRole; label: string; description: string }> = [
  { value: "super_admin", label: "Super Admin", description: "Highest access level with unrestricted control across the app and user permissions." },
  { value: "admin", label: "Admin", description: "Full access across settings, users, integrations, and project controls." },
  { value: "production", label: "Production", description: "Runs bids and projects with broader operational access." },
  { value: "operations", label: "Operations", description: "Handles staffing, CRM, schedules, and business administration." },
  { value: "artist", label: "Limited", description: "Collaborative access with a reduced settings and admin footprint." },
  { value: "standard", label: "Standard", description: "Default access for everyday team members." },
];

export const ACCESS_STATUS_OPTIONS: Array<{ value: UserStatus; label: string; description: string }> = [
  { value: "active", label: "Active", description: "Can sign in and use the app." },
  { value: "inactive", label: "Inactive", description: "Disabled in the app and equivalent to disabled in Flow Production Tracking." },
  { value: "onboarding", label: "Onboarding", description: "Joining flow in progress and not yet ready for full app access." },
  { value: "offboarding", label: "Offboarding", description: "Leaving flow in progress and no longer available for normal app use." },
];

export function getStatusToneClasses(status?: string | null) {
  switch (status) {
    case "active":
      return "border-emerald-300 bg-emerald-50 text-emerald-800";
    case "inactive":
      return "border-rose-300 bg-rose-50 text-rose-800";
    case "onboarding":
      return "border-sky-300 bg-sky-50 text-sky-800";
    case "offboarding":
      return "border-violet-300 bg-violet-50 text-violet-800";
    default:
      return "border-slate-300 bg-white text-slate-700";
  }
}

export const PERMISSION_GROUPS: Array<{
  id: string;
  label: string;
  permissions: Array<{ key: PermissionKey; label: string; description: string }>;
}> = [
  {
    id: "projects",
    label: "Projects",
    permissions: [
      { key: "projects.view", label: "View Projects", description: "Browse dashboards and open existing bids." },
      { key: "projects.edit", label: "Edit Projects", description: "Create and update bid data." },
      { key: "project_settings.manage", label: "Manage Project Settings", description: "Open and manage project-level settings and integrations." },
      { key: "project_show.view", label: "View Project Show Dashboard", description: "View cast, team, crew, and show-dashboard relationship data." },
      { key: "project_show.manage", label: "Manage Project Show Dashboard", description: "Edit cast, team, crew, and show-dashboard relationship links." },
      { key: "projects.export", label: "Export Projects", description: "Export quotes and project outputs." },
    ],
  },
  {
    id: "wiki",
    label: "Wiki",
    permissions: [
      { key: "wiki.view", label: "View Wiki", description: "Browse internal knowledge pages and search the wiki." },
      { key: "wiki.edit", label: "Edit Wiki", description: "Create, edit, reorder, and publish wiki pages." },
      { key: "wiki.manage", label: "Manage Wiki", description: "Control wiki visibility, archive pages, and manage the knowledge structure." },
    ],
  },
  {
    id: "business",
    label: "CRM and Team",
    permissions: [
      { key: "crm.view", label: "View CRM", description: "Open companies, contacts, and CRM records." },
      { key: "crm.edit", label: "Edit CRM", description: "Create and update CRM records." },
      { key: "people.view", label: "View People", description: "Browse the People and HR section." },
      { key: "people.manage", label: "Manage People", description: "Edit HR profiles and team records." },
      { key: "calendar.view", label: "View Calendar", description: "Open calendar and scheduling views." },
      { key: "calendar.manage", label: "Manage Calendar", description: "Create and manage scheduling items." },
    ],
  },
  {
    id: "admin",
    label: "Settings and Admin",
    permissions: [
      { key: "settings.view", label: "View Settings", description: "Open settings screens and view configuration." },
      { key: "settings.manage", label: "Manage Settings", description: "Change global settings and defaults." },
      { key: "integrations.manage", label: "Manage Integrations", description: "Connect or reconfigure third-party tools." },
      { key: "drive.manage", label: "Manage Drive", description: "Edit drive folder structures and permissions." },
      { key: "users.manage", label: "Manage Users", description: "Change roles, status, and access permissions." },
      { key: "trash.manage", label: "Manage Deleted Projects", description: "Restore or permanently remove deleted projects." },
    ],
  },
];

export const ALL_PERMISSION_KEYS = PERMISSION_GROUPS.flatMap(group => group.permissions.map(permission => permission.key));

export const ROLE_DEFAULT_PERMISSIONS: Record<UserRole, PermissionKey[]> = {
  super_admin: ALL_PERMISSION_KEYS,
  admin: ALL_PERMISSION_KEYS,
  production: [
    "projects.view",
    "projects.edit",
    "project_settings.manage",
    "project_show.view",
    "project_show.manage",
    "projects.export",
    "wiki.view",
    "wiki.edit",
    "crm.view",
    "people.view",
    "calendar.view",
    "calendar.manage",
    "settings.view",
    "drive.manage",
  ],
  operations: [
    "projects.view",
    "project_settings.manage",
    "project_show.view",
    "project_show.manage",
    "projects.export",
    "wiki.view",
    "wiki.edit",
    "crm.view",
    "crm.edit",
    "people.view",
    "people.manage",
    "calendar.view",
    "calendar.manage",
    "settings.view",
    "trash.manage",
  ],
  artist: [
    "projects.view",
    "projects.edit",
    "project_show.view",
    "wiki.view",
    "people.view",
    "calendar.view",
  ],
  standard: [
    "projects.view",
    "projects.edit",
    "project_show.view",
    "projects.export",
    "wiki.view",
    "people.view",
  ],
};

export function normalizeCustomPermissions(input: unknown): PermissionKey[] {
  if (!Array.isArray(input)) return [];
  return Array.from(
    new Set(
      input.filter((value): value is PermissionKey => typeof value === "string" && ALL_PERMISSION_KEYS.includes(value as PermissionKey))
    )
  );
}

export function getEffectivePermissions(role: UserRole = "standard", customPermissions: unknown = []): PermissionKey[] {
  const basePermissions = ROLE_DEFAULT_PERMISSIONS[role] || ROLE_DEFAULT_PERMISSIONS.standard;
  return Array.from(new Set([...basePermissions, ...normalizeCustomPermissions(customPermissions)]));
}

export function hasPermission(profile: Pick<UserProfile, "role" | "status" | "customPermissions"> | null | undefined, permissionKey: PermissionKey) {
  if (!profile) return false;
  if (profile.status !== "active") {
    return false;
  }
  return getEffectivePermissions(profile.role, profile.customPermissions).includes(permissionKey);
}

export function getRoleLabel(role: UserRole) {
  return ACCESS_ROLE_OPTIONS.find(option => option.value === role)?.label || "Standard";
}

export function getStatusLabel(status: UserStatus) {
  return ACCESS_STATUS_OPTIONS.find(option => option.value === status)?.label || "Active";
}
